/**
 * MapStore - Centralized state management for map components
 * Phase 3.1 of map maintainability refactor
 *
 * Uses @ngrx/component-store for local component state management.
 * This replaces the scattered state variables in SitesLocationMapComponent.
 */

import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import {
  MapLayerSettings,
  MapLabelConfig,
  LabelByOption,
  ScreenPoint,
  PictureMarker,
  BidItemMarker,
  ActivityMarker,
  DeliveryMarker,
  SelectedItemData,
  MapDateRange,
  SiteData,
  MultiItemLocationGroup
} from '../../../../../shared/types/map.types';
import { getLayerDefaults } from '../map.constants';

// ============================================================================
// State Interface
// ============================================================================

/**
 * No-location item counts for badge display
 */
export interface NoLocationCounts {
  pictures: number;
  bidItems: number;
  activities: number;
  deliveries: number;
}

/**
 * Main map state interface
 */
export interface MapState {
  // UI State
  isFullScreen: boolean;
  isSliderDragging: boolean;
  isPlaying: boolean;
  showTooltip: boolean;
  tooltipContent: string;

  // Selection State
  hoveredSiteId: string | null;
  hoveredLineItemId: string | null;
  selectedItemData: SelectedItemData | null;
  selectedBidItemMarkerId: string | null;

  // Screen coordinates
  screenPoint: ScreenPoint;
  itemPopupScreenPoint: ScreenPoint | null;
  showItemPopup: boolean;

  // Layer State
  layerSettings: MapLayerSettings;

  // Date Range
  dateRange: MapDateRange | null;

  // Site Data
  sites: SiteData[];
  filteredSites: SiteData[];
  sitesWithGeometries: string[];

  // Marker Data
  pictureMarkers: PictureMarker[];
  bidItemMarkers: BidItemMarker[];
  activityMarkers: ActivityMarker[];
  deliveryMarkers: DeliveryMarker[];

  // No-Location Items (for badges)
  noLocationCounts: NoLocationCounts;
  bidItemNoLocationItems: BidItemMarker[];
  activityNoLocationItems: ActivityMarker[];
  deliveryNoLocationItems: DeliveryMarker[];

  // Drilldown mode
  drilldownNoLocationPictures: PictureMarker[];
  drilldownNoLocationCount: number;

  // Main mode no-location pictures
  mainNoLocationPictures: PictureMarker[];
  mainNoLocationPicturesCount: number;

  // Popup content for multi-item locations
  itemPopupContent: any[];

  // Multi-item locations map
  multiItemLocations: Map<string, MultiItemLocationGroup>;

  // Label configuration
  labelConfig: MapLabelConfig;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: MapState = {
  // UI State
  isFullScreen: false,
  isSliderDragging: false,
  isPlaying: false,
  showTooltip: false,
  tooltipContent: '',

  // Selection State
  hoveredSiteId: null,
  hoveredLineItemId: null,
  selectedItemData: null,
  selectedBidItemMarkerId: null,

  // Screen coordinates
  screenPoint: { x: 0, y: 0 },
  itemPopupScreenPoint: null,
  showItemPopup: false,

  // Layer State - using centralized defaults for fullscreen mode
  layerSettings: getLayerDefaults('fullscreen'),

  // Date Range
  dateRange: null,

  // Site Data
  sites: [],
  filteredSites: [],
  sitesWithGeometries: [],

  // Marker Data
  pictureMarkers: [],
  bidItemMarkers: [],
  activityMarkers: [],
  deliveryMarkers: [],

  // No-Location Items
  noLocationCounts: {
    pictures: 0,
    bidItems: 0,
    activities: 0,
    deliveries: 0
  },
  bidItemNoLocationItems: [],
  activityNoLocationItems: [],
  deliveryNoLocationItems: [],

  // Drilldown mode
  drilldownNoLocationPictures: [],
  drilldownNoLocationCount: 0,

  // Main mode
  mainNoLocationPictures: [],
  mainNoLocationPicturesCount: 0,

  // Popup content
  itemPopupContent: [],

  // Multi-item locations
  multiItemLocations: new Map(),

  // Label configuration
  labelConfig: { enabled: false, labelBy: 'bid_item_name' }
};

// ============================================================================
// MapStore
// ============================================================================

@Injectable()
export class MapStore extends ComponentStore<MapState> {
  constructor() {
    super(initialState);
  }

  // ==========================================================================
  // Selectors - UI State
  // ==========================================================================

  readonly isFullScreen$ = this.select(state => state.isFullScreen);
  readonly isSliderDragging$ = this.select(state => state.isSliderDragging);
  readonly isPlaying$ = this.select(state => state.isPlaying);
  readonly showTooltip$ = this.select(state => state.showTooltip);
  readonly tooltipContent$ = this.select(state => state.tooltipContent);

  // ==========================================================================
  // Selectors - Selection State
  // ==========================================================================

  readonly hoveredSiteId$ = this.select(state => state.hoveredSiteId);
  readonly hoveredLineItemId$ = this.select(state => state.hoveredLineItemId);
  readonly selectedItemData$ = this.select(state => state.selectedItemData);
  readonly selectedBidItemMarkerId$ = this.select(state => state.selectedBidItemMarkerId);

  // ==========================================================================
  // Selectors - Screen Coordinates & Popup
  // ==========================================================================

  readonly screenPoint$ = this.select(state => state.screenPoint);
  readonly itemPopupScreenPoint$ = this.select(state => state.itemPopupScreenPoint);
  readonly showItemPopup$ = this.select(state => state.showItemPopup);
  readonly itemPopupContent$ = this.select(state => state.itemPopupContent);

  // ==========================================================================
  // Selectors - Layer Settings
  // ==========================================================================

  readonly layerSettings$ = this.select(state => state.layerSettings);

  readonly isPictureLayerEnabled$ = this.select(
    state => state.layerSettings.picture
  );

  readonly isBidItemLayerEnabled$ = this.select(
    state => state.layerSettings.bidItem
  );

  readonly isActivityLayerEnabled$ = this.select(
    state => state.layerSettings.activity
  );

  readonly isDeliveryLayerEnabled$ = this.select(
    state => state.layerSettings.delivery
  );

  // ==========================================================================
  // Selectors - Label Config
  // ==========================================================================

  readonly labelConfig$ = this.select(state => state.labelConfig);
  readonly isLabelsEnabled$ = this.select(state => state.labelConfig.enabled);

  // ==========================================================================
  // Selectors - Date Range
  // ==========================================================================

  readonly dateRange$ = this.select(state => state.dateRange);

  // ==========================================================================
  // Selectors - Site Data
  // ==========================================================================

  readonly sites$ = this.select(state => state.sites);
  readonly filteredSites$ = this.select(state => state.filteredSites);
  readonly sitesWithGeometries$ = this.select(state => state.sitesWithGeometries);

  // ==========================================================================
  // Selectors - Marker Data
  // ==========================================================================

  readonly pictureMarkers$ = this.select(state => state.pictureMarkers);
  readonly bidItemMarkers$ = this.select(state => state.bidItemMarkers);
  readonly activityMarkers$ = this.select(state => state.activityMarkers);
  readonly deliveryMarkers$ = this.select(state => state.deliveryMarkers);

  /**
   * Combined selector for all markers filtered by layer settings
   */
  readonly visibleMarkers$ = this.select(
    this.pictureMarkers$,
    this.bidItemMarkers$,
    this.activityMarkers$,
    this.deliveryMarkers$,
    this.layerSettings$,
    (pictures, bidItems, activities, deliveries, settings) => ({
      pictures: settings.picture ? pictures : [],
      bidItems: settings.bidItem ? bidItems : [],
      activities: settings.activity ? activities : [],
      deliveries: settings.delivery ? deliveries : []
    })
  );

  /**
   * Total count of visible markers
   */
  readonly totalVisibleMarkerCount$ = this.select(
    this.visibleMarkers$,
    (markers) =>
      markers.pictures.length +
      markers.bidItems.length +
      markers.activities.length +
      markers.deliveries.length
  );

  // ==========================================================================
  // Selectors - No-Location Items
  // ==========================================================================

  readonly noLocationCounts$ = this.select(state => state.noLocationCounts);
  readonly bidItemNoLocationItems$ = this.select(state => state.bidItemNoLocationItems);
  readonly activityNoLocationItems$ = this.select(state => state.activityNoLocationItems);
  readonly deliveryNoLocationItems$ = this.select(state => state.deliveryNoLocationItems);

  /**
   * Total count of items without location (for badge)
   */
  readonly totalNoLocationCount$ = this.select(
    this.noLocationCounts$,
    (counts) =>
      counts.bidItems + counts.activities + counts.deliveries
  );

  // ==========================================================================
  // Selectors - Drilldown Mode
  // ==========================================================================

  readonly drilldownNoLocationPictures$ = this.select(state => state.drilldownNoLocationPictures);
  readonly drilldownNoLocationCount$ = this.select(state => state.drilldownNoLocationCount);

  // ==========================================================================
  // Selectors - Main Mode
  // ==========================================================================

  readonly mainNoLocationPictures$ = this.select(state => state.mainNoLocationPictures);
  readonly mainNoLocationPicturesCount$ = this.select(state => state.mainNoLocationPicturesCount);

  // ==========================================================================
  // Updaters - UI State
  // ==========================================================================

  readonly setFullScreen = this.updater((state, isFullScreen: boolean) => ({
    ...state,
    isFullScreen
  }));

  readonly setSliderDragging = this.updater((state, isSliderDragging: boolean) => ({
    ...state,
    isSliderDragging
  }));

  readonly setPlaying = this.updater((state, isPlaying: boolean) => ({
    ...state,
    isPlaying
  }));

  readonly setTooltip = this.updater(
    (state, payload: { show: boolean; content?: string }) => ({
      ...state,
      showTooltip: payload.show,
      tooltipContent: payload.content || ''
    })
  );

  // ==========================================================================
  // Updaters - Selection State
  // ==========================================================================

  readonly setHoveredSiteId = this.updater((state, hoveredSiteId: string | null) => ({
    ...state,
    hoveredSiteId
  }));

  readonly setHoveredLineItemId = this.updater((state, hoveredLineItemId: string | null) => ({
    ...state,
    hoveredLineItemId
  }));

  readonly setSelectedItemData = this.updater((state, selectedItemData: SelectedItemData | null) => ({
    ...state,
    selectedItemData
  }));

  readonly setSelectedBidItemMarkerId = this.updater((state, selectedBidItemMarkerId: string | null) => ({
    ...state,
    selectedBidItemMarkerId
  }));

  // ==========================================================================
  // Updaters - Screen Coordinates & Popup
  // ==========================================================================

  readonly setScreenPoint = this.updater((state, screenPoint: ScreenPoint) => ({
    ...state,
    screenPoint
  }));

  readonly setItemPopup = this.updater(
    (state, payload: { show: boolean; screenPoint?: ScreenPoint; content?: any[] }) => ({
      ...state,
      showItemPopup: payload.show,
      itemPopupScreenPoint: payload.screenPoint || state.itemPopupScreenPoint,
      itemPopupContent: payload.content || state.itemPopupContent
    })
  );

  readonly closeItemPopup = this.updater((state) => ({
    ...state,
    showItemPopup: false,
    itemPopupContent: []
  }));

  // ==========================================================================
  // Updaters - Layer Settings
  // ==========================================================================

  readonly updateLayerSettings = this.updater(
    (state, settings: Partial<MapLayerSettings>) => ({
      ...state,
      layerSettings: { ...state.layerSettings, ...settings }
    })
  );

  readonly toggleLayer = this.updater(
    (state, layer: keyof MapLayerSettings) => ({
      ...state,
      layerSettings: {
        ...state.layerSettings,
        [layer]: !state.layerSettings[layer]
      }
    })
  );

  // ==========================================================================
  // Updaters - Label Config
  // ==========================================================================

  readonly setLabelConfig = this.updater((state, config: MapLabelConfig) => ({
    ...state,
    labelConfig: { ...config }
  }));

  readonly toggleLabels = this.updater((state) => ({
    ...state,
    labelConfig: { ...state.labelConfig, enabled: !state.labelConfig.enabled }
  }));

  readonly setLabelBy = this.updater((state, labelBy: LabelByOption) => ({
    ...state,
    labelConfig: { ...state.labelConfig, labelBy }
  }));

  // ==========================================================================
  // Updaters - Date Range
  // ==========================================================================

  readonly setDateRange = this.updater((state, dateRange: MapDateRange | null) => ({
    ...state,
    dateRange
  }));

  // ==========================================================================
  // Updaters - Site Data
  // ==========================================================================

  readonly setSites = this.updater((state, sites: SiteData[]) => ({
    ...state,
    sites
  }));

  readonly setFilteredSites = this.updater((state, filteredSites: SiteData[]) => ({
    ...state,
    filteredSites
  }));

  readonly setSitesWithGeometries = this.updater((state, sitesWithGeometries: string[]) => ({
    ...state,
    sitesWithGeometries
  }));

  // ==========================================================================
  // Updaters - Marker Data
  // ==========================================================================

  readonly setPictureMarkers = this.updater((state, pictureMarkers: PictureMarker[]) => ({
    ...state,
    pictureMarkers
  }));

  readonly setBidItemMarkers = this.updater((state, bidItemMarkers: BidItemMarker[]) => ({
    ...state,
    bidItemMarkers
  }));

  readonly setActivityMarkers = this.updater((state, activityMarkers: ActivityMarker[]) => ({
    ...state,
    activityMarkers
  }));

  readonly setDeliveryMarkers = this.updater((state, deliveryMarkers: DeliveryMarker[]) => ({
    ...state,
    deliveryMarkers
  }));

  // ==========================================================================
  // Updaters - No-Location Items
  // ==========================================================================

  readonly setNoLocationCounts = this.updater((state, counts: Partial<NoLocationCounts>) => ({
    ...state,
    noLocationCounts: { ...state.noLocationCounts, ...counts }
  }));

  readonly setBidItemNoLocationItems = this.updater((state, items: BidItemMarker[]) => ({
    ...state,
    bidItemNoLocationItems: items,
    noLocationCounts: { ...state.noLocationCounts, bidItems: items.length }
  }));

  readonly setActivityNoLocationItems = this.updater((state, items: ActivityMarker[]) => ({
    ...state,
    activityNoLocationItems: items,
    noLocationCounts: { ...state.noLocationCounts, activities: items.length }
  }));

  readonly setDeliveryNoLocationItems = this.updater((state, items: DeliveryMarker[]) => ({
    ...state,
    deliveryNoLocationItems: items,
    noLocationCounts: { ...state.noLocationCounts, deliveries: items.length }
  }));

  // ==========================================================================
  // Updaters - Drilldown Mode
  // ==========================================================================

  readonly setDrilldownNoLocationPictures = this.updater(
    (state, pictures: PictureMarker[]) => ({
      ...state,
      drilldownNoLocationPictures: pictures,
      drilldownNoLocationCount: pictures.length
    })
  );

  // ==========================================================================
  // Updaters - Main Mode
  // ==========================================================================

  readonly setMainNoLocationPictures = this.updater(
    (state, pictures: PictureMarker[]) => ({
      ...state,
      mainNoLocationPictures: pictures,
      mainNoLocationPicturesCount: pictures.length
    })
  );

  // ==========================================================================
  // Updaters - Multi-Item Locations
  // ==========================================================================

  readonly setMultiItemLocations = this.updater(
    (state, locations: Map<string, MultiItemLocationGroup>) => ({
      ...state,
      multiItemLocations: locations
    })
  );

  // ==========================================================================
  // Reset
  // ==========================================================================

  /**
   * Reset store to initial state
   */
  readonly reset = this.updater(() => initialState);

  /**
   * Reset only marker data (useful when changing date ranges)
   */
  readonly resetMarkers = this.updater((state) => ({
    ...state,
    pictureMarkers: [],
    bidItemMarkers: [],
    activityMarkers: [],
    deliveryMarkers: [],
    noLocationCounts: {
      pictures: 0,
      bidItems: 0,
      activities: 0,
      deliveries: 0
    },
    bidItemNoLocationItems: [],
    activityNoLocationItems: [],
    deliveryNoLocationItems: [],
    drilldownNoLocationPictures: [],
    drilldownNoLocationCount: 0,
    mainNoLocationPictures: [],
    mainNoLocationPicturesCount: 0,
    itemPopupContent: [],
    multiItemLocations: new Map()
  }));
}
